import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Cross-component coordination for the right-rail / bottom-bar
 * "dock" surface (RecordingDock + NextAppointmentDock) and the
 * floating MaieLauncherPill.
 *
 * Two responsibilities:
 *
 *   1. Mobile collision avoidance. When a bottom-anchored dock is
 *      visible (the slim "Next up · Start" bar on mobile), the
 *      launcher pill needs to sit ABOVE it instead of overlapping.
 *      Each dock instance reports its visible height; the pill reads
 *      the max and offsets itself by that + a small gap.
 *
 *   2. Desktop right-rail collapse. The right rail occupies ~460–520 px
 *      of horizontal space on `lg+`. Doctors who don't want it
 *      perpetually visible can collapse it via the in-rail toggle —
 *      collapsed state survives reload via the persist middleware,
 *      mirroring how the main Sidebar remembers its own collapsed
 *      preference.
 */

export type DockSlotId = 'recording' | 'next-appointment' | 'pending-sync';

interface DockLayoutState {
  /**
   * Per-slot height (in CSS pixels) of any bottom-anchored dock
   * currently visible. Mobile only — desktop right-rail docks don't
   * push other content vertically, so they report 0 here.
   * The launcher pill computes its offset as `max(...heights) + gap`.
   *
   * These docks are `position: fixed`, so they sit at a constant height
   * off the viewport bottom — a single height value is enough. The sticky
   * encounter "Save & sign" footer is DIFFERENT: it un-sticks and rises
   * when the page is scrolled to the very bottom, so a constant height
   * can't track it. That bar reports its live viewport rect via
   * `actionBar` instead, and the pill/widget measure their own anchor
   * to compute the lift. See useReportActionBar + useFloatingClearance.
   */
  bottomHeights: Record<DockSlotId, number>;
  /**
   * Live viewport rect (top + bottom edges, in CSS px) of a sticky page
   * action bar (the encounter "Save & sign" footer), or null when none is
   * mounted. Updated on scroll so the floating pill/widget can track the bar
   * as it un-sticks at the bottom of the page. Both edges are needed: the
   * pill lifts above the bar only while the bar overlaps the pill's corner;
   * once the bar rises clear of the corner (scrolled to the very bottom), the
   * pill drops back into the corner UNDER the bar instead of climbing into
   * the content above it.
   */
  actionBar: { top: number; bottom: number; left: number; right: number } | null;
  /**
   * Live viewport rect of the bottom-right `SynthBackgroundPill` ("Notes in
   * progress" card), or null when no synth jobs are active. Shares the corner
   * with the floating MaieLauncherPill, so the launcher clears it via the same
   * 2-axis overlap test as the action bar. Corner card, not a full-width dock —
   * hence a rect (so the bottom-LEFT FeedbackWidget doesn't lift for it).
   */
  synthCard: { top: number; bottom: number; left: number; right: number } | null;
  /** Persisted across reloads. Doctor's choice for the right-rail dock. */
  rightRailCollapsed: boolean;
  /**
   * How a LIVE capture session is presented.
   *
   *   'stage'  — full-screen consult surface. The default every time a new
   *              session starts: the doctor pressed Start on a full-screen
   *              arm panel, so dropping them straight into a 460 px rail
   *              (or, if they'd ever collapsed it, a 40 px sliver) lost the
   *              consult the moment it began.
   *   'docked' — the right rail / mobile mini-bar. Reached by pressing
   *              Minimize on the stage, so the doctor can work the chart
   *              while the mic keeps running.
   *
   * Deliberately NOT persisted: this is per-consult state, not a
   * preference. A doctor who minimized yesterday's consult should still
   * start today's on the stage.
   */
  consultView: 'stage' | 'docked';
  /**
   * Persisted across reloads. Doctor's choice for the mobile slim
   * bottom bar (the "Next up · Start" dock). When collapsed it
   * shrinks to a small corner pill so it stops covering the bottom of
   * the page (Save/Cancel CTAs etc.).
   */
  bottomBarCollapsed: boolean;

  setBottomHeight(slot: DockSlotId, height: number): void;
  setActionBar(
    rect: { top: number; bottom: number; left: number; right: number } | null,
  ): void;
  setSynthCard(
    rect: { top: number; bottom: number; left: number; right: number } | null,
  ): void;
  setRightRailCollapsed(collapsed: boolean): void;
  setConsultView(view: 'stage' | 'docked'): void;
  toggleRightRail(): void;
  setBottomBarCollapsed(collapsed: boolean): void;
}

type ClearanceRect = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

// Quantize to whole px so sub-pixel scroll jitter doesn't thrash subscribers.
const roundRect = (r: ClearanceRect | null): ClearanceRect | null =>
  r == null
    ? null
    : {
        top: Math.round(r.top),
        bottom: Math.round(r.bottom),
        left: Math.round(r.left),
        right: Math.round(r.right),
      };

const rectsEqual = (a: ClearanceRect | null, b: ClearanceRect | null): boolean =>
  a === b ||
  (!!a &&
    !!b &&
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.right === b.right);

export const useDockLayoutStore = create<DockLayoutState>()(
  persist(
    (set, get) => ({
      bottomHeights: { recording: 0, 'next-appointment': 0, 'pending-sync': 0 },
      actionBar: null,
      synthCard: null,
      rightRailCollapsed: false,
      bottomBarCollapsed: false,
      consultView: 'stage',

      setBottomHeight(slot, height) {
        const current = get().bottomHeights[slot] ?? 0;
        if (current === height) return; // no-op write — avoids needless renders
        set((state) => ({
          bottomHeights: { ...state.bottomHeights, [slot]: height },
        }));
      },

      setActionBar(rect) {
        const next = roundRect(rect);
        if (rectsEqual(get().actionBar, next)) return;
        set({ actionBar: next });
      },

      setSynthCard(rect) {
        const next = roundRect(rect);
        if (rectsEqual(get().synthCard, next)) return;
        set({ synthCard: next });
      },

      setRightRailCollapsed(collapsed) {
        set({ rightRailCollapsed: collapsed });
      },

      setConsultView(view) {
        // Minimizing must land on a dock the doctor can actually see. A
        // persisted `rightRailCollapsed` from a previous consult would
        // otherwise send them from a full screen to a 40 px sliver in one
        // tap — which reads as "the recording disappeared".
        if (view === 'docked') set({ consultView: view, rightRailCollapsed: false });
        else set({ consultView: view });
      },

      toggleRightRail() {
        set((state) => ({ rightRailCollapsed: !state.rightRailCollapsed }));
      },

      setBottomBarCollapsed(collapsed) {
        set({ bottomBarCollapsed: collapsed });
      },
    }),
    {
      name: 'maie:dock-layout',
      // Only the user preferences survive reload. Mobile bottom-dock
      // heights are recomputed on mount, so persisting them would just
      // briefly position the pill at a stale offset.
      partialize: (state) => ({
        rightRailCollapsed: state.rightRailCollapsed,
        bottomBarCollapsed: state.bottomBarCollapsed,
      }),
    },
  ),
);

/** Sum the visible bottom-dock heights into a single offset the pill
 *  can apply. Returns 0 when no dock is on screen. */
export const selectBottomDockOffset = (state: { bottomHeights: Record<DockSlotId, number> }) => {
  const max = Math.max(...Object.values(state.bottomHeights));
  return max > 0 ? max : 0;
};
